import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Outlet, Link, NavLink, useLocation } from 'react-router-dom';
import type { To } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { serviceProviderService } from '../services/serviceProviderService';
import {
  canAccessServiceModule,
  canAccessSharedRequestsModule,
  isAdmin,
} from '../utils/accessControl';
import {
  ClipboardDocumentListIcon,
  BuildingStorefrontIcon,
  LightBulbIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  MapIcon,
  Cog6ToothIcon,
  UserIcon,
  UserGroupIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';
import { Avatar } from '../components/common/UIComponents';
import { GlobalPromptArbiterProvider } from '../components/common/GlobalPromptArbiter';
import PushConsentOnboarding from '../components/common/PushConsentOnboarding';
import ReviewInvitationPrompt from '../components/common/ReviewInvitationPrompt';
import TotpNudgeCard from '../components/common/TotpNudgeCard';
import { NotificationBell } from '../components/layout/NotificationBell';
import ServiceWorkspaceHeader from '../components/Service/ServiceWorkspaceHeader';
import { BRAND_LOGO_FULL_SRC } from '../config/brandAssets';
import {
  isServiceWorkspaceRequestDetail,
  resolvePrimaryModuleFromPathname,
  resolveServiceWorkspaceActiveSection,
  SERVICE_WORKSPACE_SECTION_LABELS,
  type PrimaryModule,
} from '../utils/navigationActiveState';
import { getServiceProviderProfilePath } from '../utils/serviceProviderProfilePath';
import { canEnterProtectedCommunity } from '../shared/community';
import { useProductFeedbackCapability } from '../components/ProductFeedback/ProductFeedbackRuntimeProvider';

type NavItemConfig = {
  key: string;
  to: To;
  label: string;
  mobileLabel?: string;
  ariaLabel?: string;
  desktopClassName?: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  end?: boolean;
  isActiveOverride?: boolean;
};

const NavItem: React.FC<{ item: NavItemConfig; isMobile: boolean }> = ({ item, isMobile }) => {
  const Icon = item.icon;
  const label = isMobile ? (item.mobileLabel ?? item.label) : item.label;

  return (
    <NavLink
      to={item.to}
      end={item.end}
      aria-label={item.ariaLabel ?? label}
      title={label}
      className={({ isActive }) => {
        const isActiveFinal =
          typeof item.isActiveOverride === 'boolean' ? item.isActiveOverride : isActive;
        const sizeClass = isMobile
          ? 'gap-1 min-h-[44px] min-w-[54px] px-1.5 py-1.5 text-[11px]'
          : 'gap-1 h-[38px] min-w-[74px] px-2 py-1.5 text-xs lg:min-w-[78px] lg:px-2.5';
        const activeFolderTabClass = isMobile
          ? 'z-20 rounded-md border border-transparent bg-ratuj-300/8 text-ratuj-200 shadow-[inset_0_-1px_0_rgba(50,196,107,0.3)]'
          : 'z-20 rounded-md border border-transparent bg-ratuj-300/8 text-ratuj-200 shadow-[inset_0_-1px_0_rgba(50,196,107,0.3)]';
        const inactiveFolderTabClass =
          'nav-inactive-interactive z-10 rounded-md border border-transparent bg-transparent text-primary-light-300';
        return `nav-link relative flex shrink-0 flex-col items-center justify-center rounded-lg ${
          sizeClass
        } ${!isMobile && item.desktopClassName ? item.desktopClassName : ''} ${
          isActiveFinal ? activeFolderTabClass : inactiveFolderTabClass
        }`;
      }}
    >
      <Icon className="h-5 w-5 shrink-0" />
      <span
        className={`max-w-full truncate font-medium leading-[1.1] ${
          isMobile ? 'text-[11px]' : 'text-xs'
        }`}
      >
        {label}
      </span>
    </NavLink>
  );
};

const NavBar: React.FC = () => {
  const [hasNewSuggestions, setHasNewSuggestions] = useState(false);
  const { currentUser, userProfile, claims } = useAuth();
  const isAuthenticated = !!currentUser;
  const showServiceTab = canAccessServiceModule(userProfile, claims);
  const showSharedRequestsLink = canAccessSharedRequestsModule(userProfile, claims);
  const showAdminLinks = isAdmin(userProfile, claims);
  const canEnterCommunity = canEnterProtectedCommunity(userProfile, claims);
  const productFeedbackCapability = useProductFeedbackCapability();
  const showCommunityTopLevel = canEnterCommunity && (showAdminLinks || showServiceTab);
  const location = useLocation();
  const showNotificationBell = isAuthenticated;
  const [serviceProfileName, setServiceProfileName] = useState<string | null>(null);

  const isDiscoveryPage =
    location.pathname === '/app' ||
    location.pathname === '/app/discover' ||
    location.pathname === '/app/list' ||
    location.pathname === '/map' ||
    location.pathname === '/list';
  const activePrimaryModule = useMemo<PrimaryModule>(() => {
    return resolvePrimaryModuleFromPathname({
      pathname: location.pathname,
      search: location.search,
      currentUserId: currentUser?.uid || null,
      canAccessServiceModule: showServiceTab,
    });
  }, [currentUser?.uid, location.pathname, location.search, showServiceTab]);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !currentUser?.uid || !showServiceTab) {
      setServiceProfileName(null);
      return;
    }

    const cachedProfile = serviceProviderService.getCachedServiceProviderProfile(currentUser.uid);
    const cachedServiceName = String(cachedProfile?.serviceName || '').trim();
    if (cachedServiceName) {
      setServiceProfileName(cachedServiceName);
    } else {
      setServiceProfileName(null);
    }

    let cancelled = false;
    serviceProviderService
      .getServiceProviderProfile(currentUser.uid, {
        forceRefresh: !cachedServiceName,
      })
      .then((profile) => {
        if (cancelled) return;
        const resolvedName = String(profile?.serviceName || '').trim();
        setServiceProfileName(resolvedName || null);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Nie udało się pobrać nazwy serwisu do menu konta:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, isAuthenticated, showServiceTab]);

  const profileMenuName = showServiceTab
    ? serviceProfileName || currentUser?.displayName || 'Serwis'
    : currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Użytkownik';

  const handleLogout = async () => {
    if (!isAuthenticated) return; // Should not be possible, but good practice
    try {
      await authService.logout();
      setIsProfileMenuOpen(false);
    } catch (error) {
      console.error('Błąd podczas wylogowywania:', error);
    }
  };

  const handleGoToSuggestions = () => {
    if (isAuthenticated) {
      setHasNewSuggestions(false);
    }
    setIsProfileMenuOpen(false);
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.profile-menu') && isProfileMenuOpen) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileMenuOpen]);

  const navItems: NavItemConfig[] = [];

  if (isAuthenticated) {
    navItems.push({
      key: 'discover',
      to: {
        pathname: '/app/discover',
        search: location.search,
      },
      label: 'Odkrywaj',
      ariaLabel: 'Odkrywaj',
      icon: MapIcon,
      end: true,
      isActiveOverride: activePrimaryModule === 'discover',
    });
  }

  if (isAuthenticated && showSharedRequestsLink) {
    navItems.push({
      key: 'shared-requests',
      to: '/app/my-requests',
      label: 'Moje zlecenia',
      mobileLabel: 'Zlecenia',
      ariaLabel: 'Moje zlecenia',
      icon: ClipboardDocumentListIcon,
      isActiveOverride: activePrimaryModule === 'orders',
    });
  }

  if (isAuthenticated && showCommunityTopLevel) {
    navItems.push({
      key: 'community',
      to: '/app/community',
      label: 'Społeczność',
      mobileLabel: 'Forum',
      ariaLabel: 'Społeczność',
      desktopClassName: showServiceTab || showAdminLinks ? 'hidden xl:flex' : undefined,
      icon: UserGroupIcon,
      isActiveOverride: activePrimaryModule === 'community',
    });
  }

  if (isAuthenticated && showServiceTab) {
    navItems.push({
      key: 'service-tab',
      to: '/app/service',
      label: 'Serwis',
      ariaLabel: 'Serwis',
      icon: BuildingStorefrontIcon,
      isActiveOverride: activePrimaryModule === 'service',
    });
  }

  if (isAuthenticated && showAdminLinks) {
    navItems.push({
      key: 'admin',
      to: '/admin',
      label: 'Panel admina',
      mobileLabel: 'Admin',
      ariaLabel: 'Panel admina',
      desktopClassName: 'hidden xl:flex',
      icon: Cog6ToothIcon,
      isActiveOverride: activePrimaryModule === 'admin',
    });
  }

  const mobileNavItems =
    isAuthenticated && showServiceTab
      ? navItems.filter((item) => item.key !== 'community')
      : navItems.filter((item) => item.key !== 'admin');
  const isCommunityActive = activePrimaryModule === 'community';

  return (
    <header
      className="navbar sticky top-0 z-50 border-b border-primary-dark-300/24 bg-primary-dark-200/88 shadow-none backdrop-blur-sm"
      data-app-chrome="topbar"
    >
      <div className="px-2 [padding-left:max(env(safe-area-inset-left),0.5rem)] [padding-right:max(env(safe-area-inset-right),0.5rem)] sm:px-6 lg:px-8">
        <div className="flex min-h-[4rem] items-center justify-between md:h-[3.75rem]">
          <Link
            to={isAuthenticated ? (isDiscoveryPage ? '/' : '/app') : '/'}
            className="mr-1 flex h-11 w-11 flex-shrink-0 items-center overflow-hidden rounded-md text-xl font-display font-bold text-ratuj-300 transition-colors hover:text-ratuj-200 focus-outline md:mr-5 md:h-auto md:w-auto md:overflow-visible md:rounded-none"
            aria-label="Ratuj Sprzęt"
          >
            <img
              src={BRAND_LOGO_FULL_SRC}
              alt=""
              className="h-8 max-w-none object-left md:h-9 md:w-auto"
            />
          </Link>

          {navItems.length > 0 && (
            <>
              <nav className="mx-2 hidden min-w-0 flex-1 items-center justify-center self-stretch py-1 md:flex lg:mx-3">
                <div className="flex min-w-0 max-w-full items-center justify-center gap-1 lg:gap-1.5">
                  {navItems.map((item) => (
                    <NavItem key={item.key} item={item} isMobile={false} />
                  ))}
                </div>
              </nav>

              <nav
                aria-label="Nawigacja mobilna"
                data-testid="app-mobile-nav"
                className="flex min-w-0 flex-1 items-center justify-center self-stretch py-1 md:hidden"
              >
                <div className="flex min-w-0 items-center justify-center gap-1">
                  {mobileNavItems.map((item) => (
                    <NavItem key={item.key} item={item} isMobile />
                  ))}
                </div>
              </nav>
            </>
          )}

          <div className="relative ml-1 flex min-w-[4.5rem] flex-shrink-0 items-center justify-end gap-0.5 md:ml-4 md:min-w-[5rem] md:gap-2">
            {isAuthenticated ? (
              <>
                {showNotificationBell && <NotificationBell />}
                <div className="profile-menu">
                  <button
                    onClick={toggleProfileMenu}
                    className="flex items-center text-primary-light-300 hover:text-ratuj-300 transition-colors rounded-full focus-outline p-0.5 md:p-1"
                    aria-expanded={isProfileMenuOpen}
                    aria-haspopup="true"
                    aria-label="Otwórz menu konta"
                  >
                    <Avatar
                      src={
                        currentUser?.photoURL ||
                        currentUser?.providerData?.find((p) => p.providerId === 'google.com')
                          ?.photoURL ||
                        undefined
                      }
                      name={currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User'}
                      size="sm"
                    />
                    {hasNewSuggestions && (
                      <span className="absolute -top-1 -right-1 block h-3 w-3 z-10">
                        <LightBulbIcon className="h-3 w-3 text-yellow-400 animate-pulse" />
                      </span>
                    )}
                    <ChevronDownIcon className="hidden md:block h-4 w-4 text-primary-light-400 ml-1" />
                  </button>

                  {isProfileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 rounded-xl shadow-dropdown bg-primary-dark-200/95 backdrop-blur-sm border border-primary-dark-300/70 z-60 animate-fade-in">
                      <div className="py-1.5" role="menu" aria-orientation="vertical">
                        <div className="px-4 py-3 text-sm text-primary-light-200 border-b border-primary-dark-300/70 flex items-center">
                          <Avatar
                            // Use same fallback logic as navbar button avatar
                            src={
                              currentUser?.photoURL ||
                              currentUser?.providerData?.find((p) => p.providerId === 'google.com')
                                ?.photoURL ||
                              undefined
                            }
                            name={
                              profileMenuName ||
                              currentUser?.displayName ||
                              currentUser?.email?.split('@')[0] ||
                              'User'
                            }
                            size="md"
                            className="mr-3"
                          />
                          <div>
                            <p className="max-w-[10.75rem] whitespace-normal break-words text-sm font-medium leading-snug text-primary-light-100">
                              {profileMenuName}
                            </p>
                            <p className="text-xs text-primary-light-400 truncate mt-0.5">
                              {currentUser?.email}
                            </p>
                          </div>
                        </div>
                        <Link
                          to="/app/settings"
                          className="flex min-h-11 items-center px-4 py-2.5 text-sm text-primary-light-300 hover:bg-primary-dark-300/50 hover:text-ratuj-300"
                          role="menuitem"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          <UserIcon className="w-4 h-4 mr-2.5 text-primary-light-400" />
                          Ustawienia konta
                        </Link>
                        {isAuthenticated && canEnterCommunity && (
                          <Link
                            to="/app/community"
                            className={`flex min-h-11 items-center px-4 py-2.5 text-sm ${
                              isCommunityActive
                                ? 'bg-primary-dark-300/55 text-ratuj-300'
                                : 'text-primary-light-300 hover:bg-primary-dark-300/50 hover:text-ratuj-300'
                            }`}
                            role="menuitem"
                            aria-current={isCommunityActive ? 'page' : undefined}
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <UserGroupIcon className="w-4 h-4 mr-2.5 text-primary-light-400" />
                            Forum
                          </Link>
                        )}
                        <Link
                          to="/app/suggestions"
                          className="flex min-h-11 items-center justify-between px-4 py-2.5 text-sm text-primary-light-300 hover:bg-primary-dark-300/50 hover:text-ratuj-300"
                          role="menuitem"
                          onClick={handleGoToSuggestions}
                        >
                          <span>
                            <LightBulbIcon className="inline w-4 h-4 mr-2.5 text-primary-light-400" />{' '}
                            Sugestie
                          </span>
                          {hasNewSuggestions && (
                            <span className="ml-2 h-2 w-2 bg-yellow-400 rounded-full inline-block animate-pulse"></span>
                          )}
                        </Link>
                        {productFeedbackCapability.isAllowed && (
                          <Link
                            to="/app/product-feedback"
                            className="flex min-h-11 items-center px-4 py-2.5 text-sm text-primary-light-300 hover:bg-primary-dark-300/50 hover:text-ratuj-300"
                            role="menuitem"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <ChatBubbleBottomCenterTextIcon className="mr-2.5 h-4 w-4 text-primary-light-400" />
                            Moje zgłoszenia
                          </Link>
                        )}
                        {showAdminLinks && (
                          <Link
                            to="/admin"
                            className="flex min-h-11 items-center px-4 py-2.5 text-sm text-primary-light-300 hover:bg-primary-dark-300/50 hover:text-ratuj-300"
                            role="menuitem"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <Cog6ToothIcon className="w-4 h-4 mr-2.5 text-primary-light-400" />
                            Panel admina
                          </Link>
                        )}
                        <div className="border-t border-primary-dark-300/70 my-1"></div>
                        <button
                          onClick={handleLogout}
                          className="flex min-h-11 w-full items-center px-4 py-2.5 text-left text-sm text-primary-light-300 hover:bg-primary-dark-300/50 hover:text-status-error-500"
                          role="menuitem"
                        >
                          <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2.5 text-primary-light-400" />
                          Wyloguj się
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-lg border border-primary-dark-300/70 px-2.5 py-1.5 text-sm text-primary-light-200 transition-colors hover:bg-primary-dark-300/50 focus-outline md:min-h-0 md:min-w-0"
                >
                  <UserIcon aria-hidden="true" className="w-4 h-4" />
                  <span className="sr-only sm:not-sr-only">Zaloguj się</span>
                </Link>
                <Link
                  to="/register/service"
                  className="inline-flex min-h-11 shrink-0 items-center gap-1 whitespace-nowrap rounded-lg bg-ratuj-400 px-2.5 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-ratuj-300 focus-outline md:min-h-0"
                >
                  <BuildingStorefrontIcon aria-hidden="true" className="w-4 h-4" />
                  <span>Dołącz jako serwis</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

const AppLayout: React.FC = () => {
  const location = useLocation();
  const { currentUser, userProfile, claims } = useAuth();
  const productFeedbackCapability = useProductFeedbackCapability();
  const isMapViewportRoute =
    location.pathname === '/map' ||
    location.pathname === '/app' ||
    location.pathname === '/app/' ||
    location.pathname === '/app/discover';
  const canUseServiceWorkspace = canAccessServiceModule(userProfile, claims) && !!currentUser?.uid;
  const resolvedPrimaryModule = useMemo<PrimaryModule>(() => {
    return resolvePrimaryModuleFromPathname({
      pathname: location.pathname,
      search: location.search,
      currentUserId: currentUser?.uid || null,
      canAccessServiceModule: canUseServiceWorkspace,
    });
  }, [canUseServiceWorkspace, currentUser?.uid, location.pathname, location.search]);
  const isOwnerPreviewWorkspaceRoute =
    resolvedPrimaryModule === 'service' && location.pathname.startsWith('/service-provider/');
  const isServiceWorkspaceRequestDetailRoute = useMemo(() => {
    if (!canUseServiceWorkspace) {
      return false;
    }

    return isServiceWorkspaceRequestDetail({
      pathname: location.pathname,
      search: location.search,
    });
  }, [canUseServiceWorkspace, location.pathname, location.search]);
  const activeServiceWorkspaceSection = useMemo(() => {
    if (!canUseServiceWorkspace) {
      return null;
    }
    return resolveServiceWorkspaceActiveSection({
      pathname: location.pathname,
      search: location.search,
      isOwnerPreview: isOwnerPreviewWorkspaceRoute,
      allowCommunityWorkspace: true,
    });
  }, [canUseServiceWorkspace, isOwnerPreviewWorkspaceRoute, location.pathname, location.search]);
  const isServiceWorkspaceMode = activeServiceWorkspaceSection !== null;
  const [serviceWorkspaceName, setServiceWorkspaceName] = useState<string | null>(null);
  const [isServiceWorkspaceMenuOpen, setIsServiceWorkspaceMenuOpen] = useState(false);
  const [userCoords, setUserCoords] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [locateError, setLocateError] = useState('');
  const [resultsCount, setResultsCount] = useState(0);

  useEffect(() => {
    if (!canUseServiceWorkspace || !currentUser?.uid) {
      setServiceWorkspaceName(null);
      return;
    }

    const cachedProfile = serviceProviderService.getCachedServiceProviderProfile(currentUser.uid);
    const cachedServiceName = String(cachedProfile?.serviceName || '').trim();
    if (cachedServiceName) {
      setServiceWorkspaceName(cachedServiceName);
    } else {
      setServiceWorkspaceName(null);
    }

    let cancelled = false;
    serviceProviderService
      .getServiceProviderProfile(currentUser.uid, {
        forceRefresh: !cachedServiceName,
      })
      .then((profile) => {
        if (cancelled) return;
        const resolvedName = String(profile?.serviceName || '').trim();
        setServiceWorkspaceName(resolvedName || null);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Nie udało się pobrać nazwy serwisu do workspace headera:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [canUseServiceWorkspace, currentUser?.uid]);

  useEffect(() => {
    setIsServiceWorkspaceMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleLocationFound = useCallback((coords: { lat: number; lng: number }) => {
    setUserCoords(coords);
    setLocateError('');
  }, []);

  const handleLocationError = useCallback((message: string) => {
    setLocateError(message);
    setUserCoords(null);
  }, []);

  const handleResultsCount = useCallback((count: number) => {
    setResultsCount(count);
  }, []);

  const handleLocateErrorClear = useCallback(() => {
    setLocateError('');
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    let rafId: number | null = null;

    const applyViewportHeight = () => {
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      root.style.setProperty('--app-vh', `${viewportHeight * 0.01}px`);
    };

    const queueViewportHeightUpdate = () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        applyViewportHeight();
      });
    };

    queueViewportHeightUpdate();

    window.addEventListener('resize', queueViewportHeightUpdate);
    window.addEventListener('orientationchange', queueViewportHeightUpdate);
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', queueViewportHeightUpdate);
    visualViewport?.addEventListener('scroll', queueViewportHeightUpdate);

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener('resize', queueViewportHeightUpdate);
      window.removeEventListener('orientationchange', queueViewportHeightUpdate);
      visualViewport?.removeEventListener('resize', queueViewportHeightUpdate);
      visualViewport?.removeEventListener('scroll', queueViewportHeightUpdate);
    };
  }, []);

  const contextValue = {
    userCoords,
    setUserCoords,
    locateError,
    setLocateError,
    resultsCount,
    setResultsCount,
    handleLocationFound,
    handleLocationError,
    handleResultsCount,
    handleLocateErrorClear,
  };

  const serviceWorkspaceDisplayName = String(serviceWorkspaceName || '').trim() || 'Twój serwis';
  const serviceWorkspaceAvatarName =
    currentUser?.displayName ||
    serviceWorkspaceDisplayName ||
    currentUser?.email?.split('@')[0] ||
    'Serwis';
  const serviceWorkspaceAvatarSrc =
    currentUser?.photoURL ||
    currentUser?.providerData?.find((provider) => provider.providerId === 'google.com')?.photoURL ||
    null;
  const serviceWorkspaceItems = useMemo(() => {
    if (!currentUser?.uid) {
      return [];
    }
    return [
      {
        key: 'orders' as const,
        label: SERVICE_WORKSPACE_SECTION_LABELS.orders,
        to: '/app/service',
      },
      {
        key: 'planner' as const,
        label: SERVICE_WORKSPACE_SECTION_LABELS.planner,
        to: '/app/service/planner',
      },
      {
        key: 'service_profile' as const,
        label: SERVICE_WORKSPACE_SECTION_LABELS.service_profile,
        to: '/app/service-profile',
      },
      {
        key: 'public_profile' as const,
        label: SERVICE_WORKSPACE_SECTION_LABELS.public_profile,
        to: getServiceProviderProfilePath(currentUser.uid),
      },
      {
        key: 'settings' as const,
        label: SERVICE_WORKSPACE_SECTION_LABELS.settings,
        to: '/app/settings?workspace=service',
      },
      ...(productFeedbackCapability.isAllowed
        ? [
            {
              key: 'product_feedback' as const,
              label: SERVICE_WORKSPACE_SECTION_LABELS.product_feedback,
              to: '/app/product-feedback',
            },
          ]
        : []),
      {
        key: 'community' as const,
        label: SERVICE_WORKSPACE_SECTION_LABELS.community,
        to: '/app/community?workspace=service',
      },
    ];
  }, [currentUser?.uid, productFeedbackCapability.isAllowed]);
  const serviceWorkspaceHeaderLabel =
    isServiceWorkspaceRequestDetailRoute && activeServiceWorkspaceSection === 'orders'
      ? 'Zlecenie'
      : activeServiceWorkspaceSection
        ? SERVICE_WORKSPACE_SECTION_LABELS[activeServiceWorkspaceSection]
        : '';

  return (
    <div
      className="flex flex-col overflow-x-hidden bg-primary-dark-100 selection:bg-ratuj-300/30 selection:text-primary-light-100"
      style={{ height: 'calc(var(--app-vh, 1vh) * 100)' }}
    >
      {isServiceWorkspaceMode && activeServiceWorkspaceSection ? (
        <ServiceWorkspaceHeader
          activeSection={activeServiceWorkspaceSection}
          activeSectionLabel={serviceWorkspaceHeaderLabel}
          serviceName={serviceWorkspaceDisplayName}
          userEmail={currentUser?.email || null}
          avatarName={serviceWorkspaceAvatarName}
          avatarSrc={serviceWorkspaceAvatarSrc}
          isMenuOpen={isServiceWorkspaceMenuOpen}
          onToggleMenu={() => setIsServiceWorkspaceMenuOpen((current) => !current)}
          onCloseMenu={() => setIsServiceWorkspaceMenuOpen(false)}
          items={serviceWorkspaceItems}
          publicExitHref="/app/discover"
          compactMobileChrome={activeServiceWorkspaceSection === 'planner'}
        />
      ) : (
        <NavBar />
      )}
      <GlobalPromptArbiterProvider>
        <ReviewInvitationPrompt />
        <PushConsentOnboarding />
      </GlobalPromptArbiterProvider>
      {currentUser?.uid &&
        location.pathname.startsWith('/app') &&
        location.pathname !== '/app/settings' &&
        !location.pathname.startsWith('/app/request/') && (
          <TotpNudgeCard
            placement="shell"
            className="relative z-20 mx-3 my-2 sm:mx-4 lg:mx-auto lg:w-[calc(100%-3rem)] lg:max-w-6xl"
          />
        )}
      <main className="flex-grow min-h-0 w-full relative flex flex-col">
        <div className="pointer-events-none absolute inset-0 bg-primary-dark-100" />
        <div
          data-app-scroll-owner="true"
          className={`relative z-10 flex-grow min-h-0 overflow-x-hidden flex flex-col bg-primary-dark-100 ${
            isMapViewportRoute ? 'overflow-y-hidden overscroll-none' : 'overflow-y-auto'
          }`}
        >
          <Outlet context={contextValue} />
        </div>
      </main>
    </div>
  );
};

export default AppLayout;
